/**
 * Supabase Report Upload Service
 * Provides functions to upload report files via Edge Function
 */

import { supabase } from './client';

export interface ReportUploadData {
  // Report metadata
  legacy_id?: string; // Optional - can be null/empty for new reports (max 20 chars)
  id_group?: string; // max 20 chars
  period?: string; // max 50 chars
  report_date?: string; // ISO date string
  id_team?: string; // max 20 chars
  report_name?: string; // max 200 chars
  summary?: string; // text - no limit
  created_by_user_id?: string; // max 20 chars
  created_on?: string; // ISO datetime string
  bod_comment?: string; // max 500 chars
  file_url_temp?: string; // max 500 chars - temporary file URL
  full_url?: string; // max 500 chars - full document URL (e.g., Google Drive)
  file_id?: string; // max 200 chars - external file ID
  summary_by_ai?: string; // text - AI generated summary
  auth_user_id?: string; // UUID string - Supabase auth user ID
  user_id?: string;
  sync_source?: string; // max 100 chars - source of the data
  
  // File data
  file: File;
}

export interface ReportUploadResponse {
  success: boolean;
  message: string;
  data?: {
    report_id?: string;
    file_path?: string;
    file_url?: string;
  };
  error?: string;
}

/**
 * Upload a report file to Supabase Storage and create/update report record
 */
export async function uploadReport(reportData: ReportUploadData): Promise<ReportUploadResponse> {
  try {
    // Convert file to base64
    const fileBase64 = await fileToBase64(reportData.file);
    
    // Prepare request payload
    const payload = {
      legacy_id: reportData.legacy_id,
      id_group: reportData.id_group,
      period: reportData.period,
      report_date: reportData.report_date,
      id_team: reportData.id_team,
      report_name: reportData.report_name,
      summary: reportData.summary,
      created_by_user_id: reportData.created_by_user_id,
      created_on: reportData.created_on,
      bod_comment: reportData.bod_comment,
      file_url_temp: reportData.file_url_temp,
      full_url: reportData.full_url,
      file_id: reportData.file_id,
      summary_by_ai: reportData.summary_by_ai,
      auth_user_id: reportData.auth_user_id,
      user_id: reportData.user_id ?? reportData.auth_user_id,
      sync_source: reportData.sync_source,
      file_name: reportData.file.name,
      file_data: fileBase64,
      file_type: reportData.file.type,
    };

    // Call Edge Function
    const { data, error } = await supabase.functions.invoke('upload-report', {
      body: payload,
    });

    if (error) {
      console.error('Edge Function error:', error);
      return {
        success: false,
        message: 'Upload failed',
        error: error.message,
      };
    }

    return data as ReportUploadResponse;
  } catch (error) {
    console.error('Upload service error:', error);
    return {
      success: false,
      message: 'Upload failed',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Convert File to base64 string
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Delete a report file from storage
 */
export async function deleteReportFile(filePath: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.storage
      .from('report-files')
      .remove([filePath]);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}
